import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { changePassword } from './api';

/**
 * Rotates the operator password. Also opened automatically (non-dismissable)
 * when login reports the account still uses the well-known seeded password —
 * that credential is public knowledge (git history), so it must go.
 */
export function ChangePasswordDialog({
  open,
  forced,
  onClose,
}: {
  open: boolean;
  /** True when the seeded default password is still active — no cancel. */
  forced: boolean;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      setError('new passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'change failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={forced ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Change password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {forced && (
              <Alert severity="warning">
                This account still uses the seeded default password. It is public knowledge —
                set a new one before continuing.
              </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Current password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="New password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              helperText="At least 10 characters. Changing it signs out every other session."
              fullWidth
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {!forced && <Button onClick={onClose}>Cancel</Button>}
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !current || next.length < 10 || !confirm}
          >
            {busy ? 'Saving…' : 'Change password'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
